import React, { useEffect, useState } from 'react';
import { 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import api from '../../utils/api';

interface CheckoutSession {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  items: Array<{
    productType: string;
    item: string;
    quantity: number;
    price: number;
    productDetails?: {
      title: string;
      description: string;
      image: string;
    };
    isValid: boolean;
  }>;
  totalPrice: string;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
  transactionId?: string;
  shippingAddress?: {
    fullName?: string;
    city: string;
    street: string;
    streetNumber: string;
    flat?: string;
    notes?: string;
    phone: string;
  };
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ApiResponse {
  success: boolean;
  data: CheckoutSession[];
  pagination: Pagination;
}

export const CheckoutSessionsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<CheckoutSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 0,
    limit: 10,
    pages: 0
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [minItems, setMinItems] = useState(1);
  const [selectedSession, setSelectedSession] = useState<CheckoutSession | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchSessions = async (page: number, limit: number, sortBy: string, sortOrder: string, minItems: number) => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse>(
        `/checkout-sessions?page=${page + 1}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}&minItems=${minItems}`
      );
      setSessions(response.data.data);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      setError(t('admin.checkoutSessions.error'));
      console.error('Error fetching checkout sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(0, 10, sortBy, sortOrder, minItems);
  }, [sortBy, sortOrder, minItems]);

  const handleChangePage = (_: unknown, newPage: number) => {
    fetchSessions(newPage, pagination.limit, sortBy, sortOrder, minItems);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    fetchSessions(0, newLimit, sortBy, sortOrder, minItems);
  };

  const handleSortByChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value);
  };

  const handleSortOrderChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value as 'asc' | 'desc');
  };

  const handleMinItemsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setMinItems(Math.max(1, value));
  };

  const handleViewDetails = (session: CheckoutSession) => {
    setSelectedSession(session);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedSession(null);
  };

  const getProductTypeLabel = (type: string) => {
    switch (type) {
      case 'Tutorial':
        return t('checkout.productType.tutorial');
      case 'Workshop':
        return t('checkout.productType.workshop');
      case 'Item':
        return t('checkout.productType.item');
      case 'File':
        return t('checkout.productType.file');
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: string | number) => {
    return `₪${typeof amount === 'string' ? amount : amount.toFixed(2)}`;
  };

  return (
    <div>
      <Typography variant="h4" sx={{ mb: 3 }}>{t('admin.tabs.checkoutSessions')}</Typography>
      
      {/* Filter and Sorting Controls */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('admin.checkoutSessions.sortBy')}</InputLabel>
          <Select
            value={sortBy}
            label={t('admin.checkoutSessions.sortBy')}
            onChange={handleSortByChange}
          >
            <MenuItem value="createdAt">{t('admin.checkoutSessions.sortByCreatedAt')}</MenuItem>
            <MenuItem value="updatedAt">{t('admin.checkoutSessions.sortByUpdatedAt')}</MenuItem>
            <MenuItem value="totalPrice">{t('admin.checkoutSessions.sortByTotalPrice')}</MenuItem>
            <MenuItem value="itemCount">{t('admin.checkoutSessions.sortByItemCount')}</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('admin.checkoutSessions.sortOrder')}</InputLabel>
          <Select
            value={sortOrder}
            label={t('admin.checkoutSessions.sortOrder')}
            onChange={handleSortOrderChange}
          >
            <MenuItem value="desc">{t('admin.checkoutSessions.descending')}</MenuItem>
            <MenuItem value="asc">{t('admin.checkoutSessions.ascending')}</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label={t('admin.checkoutSessions.minItems')}
          type="number"
          value={minItems}
          onChange={handleMinItemsChange}
          inputProps={{ min: 1 }}
          sx={{ minWidth: 120 }}
        />
      </Box>
      
      {loading ? (
        <Typography>{t('admin.checkoutSessions.loading')}</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="checkout sessions table">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.checkoutSessions.sessionId')}</TableCell>
                  <TableCell>{t('admin.checkoutSessions.customer')}</TableCell>
                  <TableCell>{t('admin.checkoutSessions.itemCount')}</TableCell>
                  <TableCell>{t('admin.checkoutSessions.totalPrice')}</TableCell>
                  <TableCell>{t('admin.checkoutSessions.createdAt')}</TableCell>
                  <TableCell>{t('admin.checkoutSessions.transactionId')}</TableCell>
                  <TableCell>{t('admin.checkoutSessions.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow key={session._id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {session._id.slice(-8)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {session.userId ? (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {session.userId.firstName} {session.userId.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {session.userId.email}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {t('admin.checkoutSessions.deletedUser')}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={session.itemCount} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(session.totalPrice)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(session.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {session.transactionId ? session.transactionId.slice(-8) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('admin.checkoutSessions.viewDetails')}>
                        <IconButton 
                          size="small"
                          onClick={() => handleViewDetails(session)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.limit}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
          />
        </Paper>
      )}

      {/* Session Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('admin.checkoutSessions.sessionId')}: {selectedSession?._id.slice(-8)}
        </DialogTitle>
        <DialogContent>
          {selectedSession && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('admin.checkoutSessions.customer')}
                </Typography>
                {selectedSession.userId ? (
                  <>
                    <Typography variant="body2">
                      <strong>{selectedSession.userId.firstName} {selectedSession.userId.lastName}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedSession.userId.email}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('admin.checkoutSessions.deletedUser')}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('admin.checkoutSessions.items')} ({selectedSession.itemCount})
                </Typography>
                <List>
                  {selectedSession.items.map((item, index) => (
                    <ListItem key={index} divider>
                      <ListItemAvatar>
                        <Avatar 
                          src={item.productDetails?.image} 
                          alt={item.productDetails?.title}
                          variant="rounded"
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {item.productDetails?.title || 'Unknown Product'}
                            </Typography>
                            <Chip 
                              label={getProductTypeLabel(item.productType)} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('admin.checkoutSessions.quantity')}: {item.quantity} | {t('admin.checkoutSessions.price')}: {formatCurrency(item.price)}
                            </Typography>
                            {item.productDetails?.description && (
                              <Typography variant="caption" display="block">
                                {item.productDetails.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {selectedSession.shippingAddress && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('admin.checkoutSessions.shippingAddress')}
                  </Typography>
                  {selectedSession.shippingAddress.fullName && (
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {selectedSession.shippingAddress.fullName}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {selectedSession.shippingAddress.street} {selectedSession.shippingAddress.streetNumber}
                    {selectedSession.shippingAddress.flat
                      ? `, ${t('admin.checkoutSessions.flat')} ${selectedSession.shippingAddress.flat}`
                      : ''}
                  </Typography>
                  <Typography variant="body2">
                    {selectedSession.shippingAddress.city}
                  </Typography>
                  <Typography variant="body2">
                    {t('admin.checkoutSessions.phone')}: {selectedSession.shippingAddress.phone}
                  </Typography>
                  {selectedSession.shippingAddress.notes && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedSession.shippingAddress.notes}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {t('admin.checkoutSessions.totalPrice')}: {formatCurrency(selectedSession.totalPrice)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.checkoutSessions.createdAt')}: {formatDate(selectedSession.createdAt)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
